import React, { useEffect, useState, useCallback } from 'react';
import SearchFilterBar from '../components/SearchFilterBar';
import Pagination from '../components/Pagination';
import CompanyForm from '../components/CompanyForm';
import {
  listCompanies,
  createCompany,
  updateCompany,
  deleteCompany,
  toggleFavouriteCompany,
} from '../salesKitAPI';

const STATUS_STYLE = {
  lead: 'bg-slate-500/15 text-slate-300 ring-slate-500/30',
  contacted: 'bg-sky-500/15 text-sky-300 ring-sky-500/30',
  negotiation: 'bg-amber-500/15 text-amber-300 ring-amber-500/30',
  converted: 'bg-emerald-500/15 text-emerald-300 ring-emerald-500/30',
  lost: 'bg-rose-500/15 text-rose-300 ring-rose-500/30',
  inactive: 'bg-white/10 text-white/40 ring-white/10',
};

function StatusBadge({ status }) {
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold ring-1 ${STATUS_STYLE[status] || STATUS_STYLE.lead}`}>
      {status}
    </span>
  );
}

export default function CompaniesPage({ onBack }) {
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1 });
  const [search, setSearch] = useState('');
  const [salesStage, setSalesStage] = useState('');
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async (page = 1) => {
    setLoading(true);
    setError('');
    try {
      const data = await listCompanies({ search, salesStage, page, limit: 10 });
      setItems(data.items);
      setPagination({ page: data.pagination.page, totalPages: data.pagination.totalPages });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [search, salesStage]);

  useEffect(() => {
    const t = setTimeout(() => load(1), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, salesStage]);

  const handleSave = async (form) => {
    if (editing && editing !== 'new') await updateCompany(editing._id, form);
    else await createCompany(form);
    setEditing(null);
    load(pagination.page);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this company?')) return;
    await deleteCompany(id);
    load(pagination.page);
  };

  const handleFavourite = async (id) => {
    await toggleFavouriteCompany(id);
    load(pagination.page);
  };

  return (
    <div className="min-h-screen bg-[#0B0B12] text-white px-4 py-6 sm:px-8 sm:py-8">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <button onClick={onBack} className="text-sm text-white/50 hover:text-white transition flex items-center gap-1">
            ← Back
          </button>
          <h2 className="text-xl font-bold tracking-tight">Companies</h2>
          <span className="w-12" />
        </div>

        <SearchFilterBar
          search={search}
          onSearchChange={setSearch}
          filters={[{ name: 'salesStage', label: 'All Statuses', value: salesStage, onChange: setSalesStage, options: Object.keys(STATUS_STYLE) }]}
          onAddClick={() => setEditing('new')}
          addLabel="Add Company"
        />

        {error && <p className="text-rose-400 text-sm mb-3">{error}</p>}

        {loading ? (
          <p className="text-white/40 text-sm">Loading…</p>
        ) : !items.length ? (
          <div className="text-center py-16 text-white/30 rounded-3xl ring-1 ring-white/5 bg-white/[0.02]">
            No companies yet — hit "+ Add Company" to create your first lead.
          </div>
        ) : (
          <>
            {/* Desktop / tablet: table */}
            <div className="hidden md:block overflow-x-auto rounded-2xl ring-1 ring-white/10 bg-[#14141C]">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-widest text-white/40 border-b border-white/5">
                    <th className="px-4 py-3 font-semibold">Company</th>
                    <th className="px-4 py-3 font-semibold">Contact</th>
                    <th className="px-4 py-3 font-semibold">Location</th>
                    <th className="px-4 py-3 font-semibold">Status</th>
                    <th className="px-4 py-3 font-semibold">★</th>
                    <th className="px-4 py-3 font-semibold text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((c) => (
                    <tr key={c._id} className="border-b border-white/5 last:border-0 hover:bg-white/[0.03] transition">
                      <td className="px-4 py-3 font-medium">{c.businessName}</td>
                      <td className="px-4 py-3 text-white/60">
                        {c.contactPerson || '—'}
                        <div className="text-xs text-white/30">{c.phone || c.email}</div>
                      </td>
                      <td className="px-4 py-3 text-white/60">{[c.city, c.state].filter(Boolean).join(', ') || '—'}</td>
                      <td className="px-4 py-3"><StatusBadge status={c.salesStage} /></td>
                      <td className="px-4 py-3">
                        <button onClick={() => handleFavourite(c._id)} className="text-lg">
                          {c.isFavourite ? '⭐' : <span className="text-white/20">☆</span>}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <button onClick={() => setEditing(c)} className="text-violet-400 hover:text-violet-300 text-xs font-semibold mr-3">Edit</button>
                        <button onClick={() => handleDelete(c._id)} className="text-rose-400 hover:text-rose-300 text-xs font-semibold">Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile: cards, not a squished table */}
            <div className="md:hidden space-y-3">
              {items.map((c) => (
                <div key={c._id} className="rounded-2xl bg-[#14141C] ring-1 ring-white/10 p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-semibold">{c.businessName}</p>
                      <p className="text-xs text-white/40 mt-0.5">{[c.city, c.state].filter(Boolean).join(', ') || 'No location'}</p>
                    </div>
                    <button onClick={() => handleFavourite(c._id)} className="text-lg shrink-0">
                      {c.isFavourite ? '⭐' : <span className="text-white/20">☆</span>}
                    </button>
                  </div>

                  <div className="flex items-center justify-between mt-3">
                    <StatusBadge status={c.salesStage} />
                    <span className="text-xs text-white/40">{c.phone || c.email || ''}</span>
                  </div>

                  <div className="flex gap-2 mt-4">
                    <button onClick={() => setEditing(c)} className="flex-1 py-2 rounded-xl bg-white/5 ring-1 ring-white/10 text-xs font-semibold text-white/80">
                      Edit
                    </button>
                    <button onClick={() => handleDelete(c._id)} className="flex-1 py-2 rounded-xl bg-rose-500/10 ring-1 ring-rose-500/30 text-xs font-semibold text-rose-400">
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        <Pagination page={pagination.page} totalPages={pagination.totalPages} onChange={load} />

        {editing && (
          <CompanyForm initial={editing === 'new' ? null : editing} onSave={handleSave} onClose={() => setEditing(null)} />
        )}
      </div>
    </div>
  );
}
